import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { AiApplicationDto } from '@terra/shared-contracts-ai-applications';
import type { ExternalNetworkDto } from '@terra/shared-contracts-external-network';
import type { InternalNetworkDto } from '@terra/shared-contracts-internal-network';
import { isAttackSurfaceEnabled } from '@terra/shared-contracts-cockpit';
import { applicationsClient } from '@terra/shared-rest-clients-applications';
import {
  createApiHeaders,
  withOrchestratorClient,
} from '../clients/tester-client/api-core';
import { terraProxyApiClient } from '../clients/testerClient';
import { useFeatureFlagsContext } from '../app/providers/FeatureFlagsProvider';
import { useUserTenants } from './api/useUserTenants';
import { useSelectedCompany } from '../store/projectStore';
import { getCurrentTenantId } from '../store/tenantStorage';
import { filterTargetsForTenant } from '../utils/filter-targets-for-tenant';
import { isApplicationActivated } from '../utils/isApplicationActivated';
import { mergePortalTargets } from '../utils/portal-target.utils';
import type { Application } from '../store/projectStore';

export const TARGETS_QUERY_KEY = 'targets';
export const AI_APPLICATIONS_LICENSE_QUERY_KEY = 'ai-applications-licensed';
const TARGETS_STALE_TIME_MS = 60 * 1000;
const TARGETS_GC_TIME_MS = 60000;

export type CatalogSliceStatus = 'disabled' | 'loaded' | 'failed';

export type TargetsCatalogSliceStatus = {
  aiApplications: CatalogSliceStatus;
  externalNetworks: CatalogSliceStatus;
  internalNetworks: CatalogSliceStatus;
};

type TargetsQueryResult = {
  targets: Application[];
  catalogSliceStatus: TargetsCatalogSliceStatus;
};

const DISABLED_CATALOG_SLICE_STATUS: TargetsCatalogSliceStatus = {
  aiApplications: 'disabled',
  externalNetworks: 'disabled',
  internalNetworks: 'disabled',
};

export const resolveAiApplicationsLicensed = async (): Promise<boolean> => {
  try {
    const response = await applicationsClient.getLicenseUtilization({
      ...withOrchestratorClient(await createApiHeaders()),
    });

    if (response.status !== 200) {
      throw new Error(
        `Failed to resolve AI application license: status ${response.status}`,
      );
    }

    const support = response.body.license?.aiApplicationSupport;

    return support != null && isAttackSurfaceEnabled(support);
  } catch (error: unknown) {
    console.warn('[useTargets] Failed to resolve AI application license', {
      error: error instanceof Error ? error : new Error(String(error)),
    });

    throw error instanceof Error ? error : new Error(String(error));
  }
};

export function useTargets({ companyId }: { companyId: string | undefined }) {
  const selectedCompany = useSelectedCompany();
  const { currentTenantId: authTenantId, isFetched: isTenantsFetched } =
    useUserTenants();
  const { featureFlags, isFetched: isFeatureFlagsFetched } =
    useFeatureFlagsContext();
  const externalSurfaceEnabled = Boolean(featureFlags.EXTERNAL_NETWORK_ENABLED);
  const internalNetworkPentestEnabled = Boolean(
    featureFlags.INTERNAL_NETWORK_PENTEST_ENABLED,
  );
  const queryClient = useQueryClient();
  const invalidateTargets = () => {
    queryClient.invalidateQueries({ queryKey: [TARGETS_QUERY_KEY] });
    queryClient.invalidateQueries({
      queryKey: [AI_APPLICATIONS_LICENSE_QUERY_KEY],
    });
  };

  const aiLicenseQuery = useQuery({
    queryKey: [AI_APPLICATIONS_LICENSE_QUERY_KEY, selectedCompany?.tenantId],
    queryFn: resolveAiApplicationsLicensed,
    enabled: !!selectedCompany?.tenantId,
    staleTime: TARGETS_STALE_TIME_MS,
    gcTime: TARGETS_GC_TIME_MS,
  });

  const isAiLicenseResolved =
    aiLicenseQuery.isSuccess || aiLicenseQuery.isError;
  const aiApplicationsLicensed = aiLicenseQuery.isSuccess
    ? aiLicenseQuery.data
    : aiLicenseQuery.isError
      ? false
      : undefined;

  const query = useQuery({
    queryKey: [
      TARGETS_QUERY_KEY,
      companyId,
      selectedCompany?.tenantId,
      authTenantId,
      isTenantsFetched,
      externalSurfaceEnabled,
      internalNetworkPentestEnabled,
      aiApplicationsLicensed ?? null,
    ],
    queryFn: async (): Promise<TargetsQueryResult> => {
      if (!selectedCompany?.tenantId) {
        console.warn('No company selected - cannot fetch targets');

        return {
          targets: [],
          catalogSliceStatus: DISABLED_CATALOG_SLICE_STATUS,
        };
      }

      const currentTenantId = getCurrentTenantId(
        isTenantsFetched ? authTenantId : undefined,
      );

      if (currentTenantId && selectedCompany.tenantId !== currentTenantId) {
        console.warn(
          '[useTargets] Selected company tenant does not match current tenant',
        );

        return {
          targets: [],
          catalogSliceStatus: DISABLED_CATALOG_SLICE_STATUS,
        };
      }

      const webApplicationsResponse =
        await terraProxyApiClient.getAllApplications();

      if (webApplicationsResponse.status !== 200) {
        throw new Error(
          `Failed to fetch applications: status ${webApplicationsResponse.status}`,
        );
      }

      const licenseQueryState = queryClient.getQueryState<boolean>([
        AI_APPLICATIONS_LICENSE_QUERY_KEY,
        selectedCompany.tenantId,
      ]);
      const aiApplicationsEnabled =
        licenseQueryState?.status === 'success' &&
        licenseQueryState.data === true;

      let aiApplications: AiApplicationDto[] = [];
      let externalSurfaces: ExternalNetworkDto[] = [];
      let internalNetworks: InternalNetworkDto[] = [];
      let aiApplicationsStatus: CatalogSliceStatus = aiApplicationsEnabled
        ? 'failed'
        : 'disabled';
      let externalNetworksStatus: CatalogSliceStatus = externalSurfaceEnabled
        ? 'failed'
        : 'disabled';
      let internalNetworksStatus: CatalogSliceStatus =
        internalNetworkPentestEnabled ? 'failed' : 'disabled';

      if (aiApplicationsEnabled) {
        try {
          const aiApplicationsResponse =
            await terraProxyApiClient.getAiApplications();

          if (aiApplicationsResponse.status === 200) {
            aiApplications = aiApplicationsResponse.body.data;
            aiApplicationsStatus = 'loaded';
          } else {
            console.warn('[useTargets] Failed to fetch AI applications', {
              status: aiApplicationsResponse.status,
            });
          }
        } catch (error: unknown) {
          console.warn('[useTargets] Failed to fetch AI applications', {
            error: error instanceof Error ? error : new Error(String(error)),
          });
        }
      }

      if (externalSurfaceEnabled) {
        try {
          const externalSurfacesResponse =
            await terraProxyApiClient.getExternalNetworks();

          if (externalSurfacesResponse.status === 200) {
            externalSurfaces = externalSurfacesResponse.body.data;
            externalNetworksStatus = 'loaded';
          } else {
            console.warn('[useTargets] Failed to fetch external surfaces', {
              status: externalSurfacesResponse.status,
            });
          }
        } catch (error: unknown) {
          console.warn('[useTargets] Failed to fetch external surfaces', {
            error: error instanceof Error ? error : new Error(String(error)),
          });
        }
      }

      if (internalNetworkPentestEnabled) {
        try {
          const internalNetworksResponse =
            await terraProxyApiClient.getInternalNetworks();

          if (internalNetworksResponse.status === 200) {
            internalNetworks = internalNetworksResponse.body.data;
            internalNetworksStatus = 'loaded';
          } else {
            console.warn(
              '[useTargets] Failed to fetch internal network surfaces',
              {
                status: internalNetworksResponse.status,
              },
            );
          }
        } catch (error: unknown) {
          console.warn(
            '[useTargets] Failed to fetch internal network surfaces',
            {
              error: error instanceof Error ? error : new Error(String(error)),
            },
          );
        }
      }

      return {
        targets: filterTargetsForTenant({
          targets: mergePortalTargets({
            webApplications: webApplicationsResponse.body,
            aiApplications,
            externalSurfaces,
            internalNetworks,
          }).filter(isApplicationActivated),
          tenantId: currentTenantId ?? selectedCompany.tenantId,
        }),
        catalogSliceStatus: {
          aiApplications: aiApplicationsStatus,
          externalNetworks: externalNetworksStatus,
          internalNetworks: internalNetworksStatus,
        },
      };
    },
    enabled:
      !!selectedCompany?.tenantId &&
      isFeatureFlagsFetched &&
      isAiLicenseResolved &&
      isTenantsFetched,
    staleTime: TARGETS_STALE_TIME_MS,
    gcTime: TARGETS_GC_TIME_MS,
  });

  return {
    targets: query.data?.targets ?? [],
    catalogSliceStatus:
      query.data?.catalogSliceStatus ?? DISABLED_CATALOG_SLICE_STATUS,
    isLoading: aiLicenseQuery.isLoading || query.isLoading,
    isFetched: isAiLicenseResolved && query.isFetched,
    isFetching: aiLicenseQuery.isFetching || query.isFetching,
    error: query.error,
    refetch: async () => {
      await queryClient.refetchQueries({
        queryKey: [AI_APPLICATIONS_LICENSE_QUERY_KEY],
      });

      return queryClient.refetchQueries({
        queryKey: [TARGETS_QUERY_KEY],
      });
    },
    invalidateTargets,
  };
}

export type TargetItem = Application;

export function useApplications({
  companyId,
}: {
  companyId: string | undefined;
}) {
  const result = useTargets({ companyId });

  return {
    applications: result.targets,
    isLoading: result.isLoading,
    isFetched: result.isFetched,
    isFetching: result.isFetching,
    error: result.error,
    refetch: result.refetch,
    invalidateApplications: result.invalidateTargets,
  };
}

export type ApplicationItem = Application;
